import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  Ban,
  Check,
  CloudDownload,
  DollarSign,
  Plus,
  RefreshCw,
  Split,
  Star,
} from 'lucide-react';
import { Action } from '../../../../components/MultipleActionsInterfaces';
import LangEnum from '../../../../lang/LangEnum';
import { Role } from '../types/actions.types';
import { ActionEnum, CopyOrderActionType } from '../types/orderActions.types';
import { allRoles, brandRoles } from './actions.utils';

export enum OrderListMassActionsEnum {
  FINISH_TO_IN_REVISION = 'FINISH_TO_IN_REVISION',
  FINISH_TO_ON_APPROVAL = 'FINISH_TO_ON_APPROVAL',
  APPROVE_ORDERS = 'APPROVE_ORDERS',
  MOVE_TO_SENT = 'MOVE_TO_SENT',
  BACK_TO_DRAFT = 'BACK_TO_DRAFT',
  BACK_TO_IN_REVISION = 'BACK_TO_IN_REVISION',
  BACK_TO_ON_APPROVAL = 'BACK_TO_ON_APPROVAL',
  BACK_TO_APPROVED = 'BACK_TO_APPROVED',
  CANCEL_ORDERS = 'CANCEL_ORDERS',
  ARCHIVE_ORDERS = 'ARCHIVE_ORDERS',
  UNARCHIVE_ORDERS = 'UNARCHIVE_ORDERS',
  EXPORT_MULTIPLE_ORDERS = 'EXPORT_MULTIPLE_ORDERS',
  UPDATE_PRICES = 'UPDATE_PRICES',
  UPDATE_CONDITIONS = 'UPDATE_CONDITIONS',
  SPLIT_ORDERS = 'SPLIT_ORDERS',
  COPY_ORDERS = 'COPY_ORDERS',
  COPY_ORDERS_AS_SUGGESTED = 'COPY_ORDERS_AS_SUGGESTED',
}

export const COPY_ORDERS_MASS_ACTIONS = [
  OrderListMassActionsEnum.COPY_ORDERS,
  OrderListMassActionsEnum.COPY_ORDERS_AS_SUGGESTED,
];

export const COPY_ACTION_BY_MASS_ACTION: Partial<
  Record<OrderListMassActionsEnum, CopyOrderActionType>
> = {
  [OrderListMassActionsEnum.COPY_ORDERS]: ActionEnum.COPY_AS_DRAFT,
  [OrderListMassActionsEnum.COPY_ORDERS_AS_SUGGESTED]: ActionEnum.COPY_AS_SUGGESTED,
};

export const orderListMassActions: {
  [key in OrderListMassActionsEnum]: Action<OrderListMassActionsEnum>;
} = {
  [OrderListMassActionsEnum.FINISH_TO_IN_REVISION]: {
    label: LangEnum.ORDER_MASS_ACTIONS_FINISH_ORDERS_LABEL,
    icon: ArrowRight,
    type: OrderListMassActionsEnum.FINISH_TO_IN_REVISION,
    isMassAction: true,
    globalRole: [Role.ADMIN_LOJA, Role.COMPRADOR],
  },
  [OrderListMassActionsEnum.FINISH_TO_ON_APPROVAL]: {
    label: LangEnum.ORDER_MASS_ACTIONS_FINISH_ORDERS_LABEL,
    icon: ArrowRight,
    type: OrderListMassActionsEnum.FINISH_TO_ON_APPROVAL,
    isMassAction: true,
    globalRole: brandRoles,
  },
  [OrderListMassActionsEnum.APPROVE_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_APPROVE_ORDERS_LABEL,
    icon: Check,
    type: OrderListMassActionsEnum.APPROVE_ORDERS,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
    confirmModalParams: {
      label: LangEnum.ORDER_MASS_ACTIONS_APPROVE_THE_ORDERS_LABEL,
      acceptLabel: LangEnum.GLOBAL_APPROVE,
      denyLabel: LangEnum.GLOBAL_CANCEL_LABEL,
    },
  },
  [OrderListMassActionsEnum.MOVE_TO_SENT]: {
    label: LangEnum.ORDER_SUMMARY_MOVE_TO_SENT_ACTION_LABEL,
    icon: ArrowRight,
    type: OrderListMassActionsEnum.MOVE_TO_SENT,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
    globalAbility: ['move-order-status-to-sent'],
  },
  [OrderListMassActionsEnum.BACK_TO_DRAFT]: {
    label: LangEnum.ORDER_SUMMARY_BACK_TO_DRAFT_ACTION_LABEL,
    icon: ArrowLeft,
    type: OrderListMassActionsEnum.BACK_TO_DRAFT,
    isMassAction: true,
    globalRole: brandRoles,
  },
  [OrderListMassActionsEnum.BACK_TO_IN_REVISION]: {
    label: LangEnum.ORDER_SUMMARY_BACK_TO_REVISION_ACTION_LABEL,
    icon: ArrowLeft,
    type: OrderListMassActionsEnum.BACK_TO_IN_REVISION,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
  },
  [OrderListMassActionsEnum.BACK_TO_ON_APPROVAL]: {
    label: LangEnum.ORDER_SUMMARY_BACK_TO_APPROVAL_ACTION_LABEL,
    icon: ArrowLeft,
    type: OrderListMassActionsEnum.BACK_TO_ON_APPROVAL,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
  },
  [OrderListMassActionsEnum.BACK_TO_APPROVED]: {
    label: LangEnum.ORDER_SUMMARY_BACK_TO_APPROVED_ACTION_LABEL,
    icon: ArrowLeft,
    type: OrderListMassActionsEnum.BACK_TO_APPROVED,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
    globalAbility: ['move-order-status-to-approved'],
  },
  [OrderListMassActionsEnum.CANCEL_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_CANCEL_ORDERS_LABEL,
    icon: Ban,
    type: OrderListMassActionsEnum.CANCEL_ORDERS,
    isMassAction: true,
    globalRole: allRoles,
    globalAbility: ['move-order-status-to-canceled'],
  },
  [OrderListMassActionsEnum.ARCHIVE_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_ARCHIVE_ORDERS_LABEL,
    icon: Archive,
    type: OrderListMassActionsEnum.ARCHIVE_ORDERS,
    isMassAction: true,
    globalRole: brandRoles,
  },
  [OrderListMassActionsEnum.UNARCHIVE_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_UNARCHIVE_ORDERS_LABEL,
    icon: ArchiveRestore,
    type: OrderListMassActionsEnum.UNARCHIVE_ORDERS,
    isMassAction: true,
    globalRole: brandRoles,
  },
  [OrderListMassActionsEnum.EXPORT_MULTIPLE_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_EXPORT_ORDERS_LABEL,
    icon: CloudDownload,
    type: OrderListMassActionsEnum.EXPORT_MULTIPLE_ORDERS,
    isMassAction: true,
    globalRole: allRoles,
    globalAbility: ['export-order-pdf'],
  },
  [OrderListMassActionsEnum.UPDATE_PRICES]: {
    label: LangEnum.ORDER_MASS_ACTIONS_UPDATE_PRICES_LABEL,
    icon: DollarSign,
    type: OrderListMassActionsEnum.UPDATE_PRICES,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
  },
  [OrderListMassActionsEnum.UPDATE_CONDITIONS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_UPDATE_CONDITIONS_LABEL,
    icon: RefreshCw,
    type: OrderListMassActionsEnum.UPDATE_CONDITIONS,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
  },
  [OrderListMassActionsEnum.SPLIT_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_SPLIT_ORDERS_LABEL,
    icon: Split,
    type: OrderListMassActionsEnum.SPLIT_ORDERS,
    isMassAction: true,
    globalRole: [Role.ADMIN_MARCA],
  },
  [OrderListMassActionsEnum.COPY_ORDERS]: {
    label: LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_LABEL,
    icon: Plus,
    type: OrderListMassActionsEnum.COPY_ORDERS,
    isMassAction: true,
    globalRole: allRoles,
    globalAbility: ['orders'],
  },
  [OrderListMassActionsEnum.COPY_ORDERS_AS_SUGGESTED]: {
    label: LangEnum.ORDER_MASS_ACTIONS_COPY_ORDERS_AS_SUGGESTED_LABEL,
    icon: Star,
    type: OrderListMassActionsEnum.COPY_ORDERS_AS_SUGGESTED,
    isMassAction: true,
    globalRole: allRoles,
    globalAbility: ['orders'],
  },
};
